import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Star, Coins, Check, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import useUserStore from '@/store/userStore';
import getOrCreateFingerprint from '@/utils/fingerprint';

const SignupModal = ({ open, onOpenChange, initialMode = 'signup' }) => {
  const [mode, setMode] = useState(initialMode);
  const [discountCode, setDiscountCode] = useState(null);
  const [fingerprintBlocked, setFingerprintBlocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, register: registerUser } = useUserStore();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setDiscountCode(null);
      setFingerprintBlocked(false);
      setLoginError(null);
      reset(); // Reset form when modal opens
    }
  }, [open, initialMode, reset]);

  const onSignup = async (data) => {
    setIsSubmitting(true);
    
    // Get device fingerprint
    let fingerprint = null;
    try {
      fingerprint = await getOrCreateFingerprint();
    } catch (e) {
      console.log('Fingerprint generation failed, proceeding without it');
    }
    
    const result = await registerUser(data.name, data.email, data.password, fingerprint);
    
    if (result.success) {
      if (result.data.fingerprint_blocked) {
        setFingerprintBlocked(true);
        setDiscountCode(null);
        toast.success('Account created! Start earning rewards.');
      } else {
        setDiscountCode(result.data.discount_code);
        toast.success('Account created! Welcome to AquaPlants Plus!');
      }
    } else {
      toast.error(result.error);
    }
    setIsSubmitting(false);
  };

  const onLogin = async (data) => {
    setLoginError(null);
    setIsSubmitting(true);
    const result = await login(data.email, data.password);
    
    if (result.success) {
      toast.success(`Welcome back, ${result.data.name}!`);
      onOpenChange(false);
      reset();
    } else {
      setLoginError(result.error || 'Invalid email or password');
      toast.error(result.error || 'Login failed');
    }
    setIsSubmitting(false);
  };

  const switchMode = () => {
    setMode(mode === 'signup' ? 'login' : 'signup');
    reset();
  };

  // Success screen after signup (with or without discount)
  if (discountCode || fingerprintBlocked) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl md:text-3xl font-bold text-center">
              {fingerprintBlocked ? 'Welcome!' : 'Welcome to the Family!'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className={`w-16 h-16 md:w-20 md:h-20 ${fingerprintBlocked ? 'bg-amber-100' : 'bg-primary/10'} rounded-full flex items-center justify-center mx-auto mb-5`}>
              {fingerprintBlocked ? (
                <Star className="w-8 h-8 md:w-10 md:h-10 text-amber-600" />
              ) : (
                <Gift className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              )}
            </div>
            
            {!fingerprintBlocked && discountCode && (
              <>
                <h3 className="text-xl md:text-2xl font-bold mb-4">Your 15% Off Code:</h3>
                <div className="bg-secondary/50 rounded-lg p-4 md:p-6 mb-5">
                  <p className="text-2xl md:text-3xl font-bold mono-font text-primary" data-testid="discount-code">{discountCode}</p>
                </div>
              </>
            )}

            {fingerprintBlocked && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">New account, but no first-order discount</p>
                    <p className="text-sm text-amber-700 mt-1">
                      It looks like a discount was already claimed from this device. But don't worry — you can still earn Plant Perks on every purchase!
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 mb-5 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Coins className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-800">🌿 Plant Perks Activated!</span>
              </div>
              <ul className="text-sm text-amber-700 space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> Earn 2 perks per $1 spent
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> Redeem 100 perks for $5 off
                </li>
              </ul>
            </div>
            
            <Button onClick={() => { onOpenChange(false); setDiscountCode(null); setFingerprintBlocked(false); }} className="w-full rounded-full" data-testid="start-shopping-btn">
              Start Shopping
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="signup-modal">
        <DialogHeader>
          <DialogTitle className="heading-font text-2xl md:text-3xl font-bold">
            {mode === 'signup' ? 'Join & Save' : 'Welcome Back'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 md:py-4">
          {mode === 'signup' && (
            <div className="bg-gradient-to-r from-primary/10 to-green-50 rounded-lg p-4 md:p-5 mb-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-base md:text-lg">15% Off First Order</p>
                  <p className="text-sm text-muted-foreground">Instant discount code when you sign up</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🌿</span>
                </div>
                <div>
                  <p className="font-semibold text-base md:text-lg">Plant Perks Rewards</p>
                  <p className="text-sm text-muted-foreground">2 perks per $1 • 100 perks = $5 off</p>
                </div>
              </div>
              <div className="flex items-start gap-3 mt-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Coins className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-base md:text-lg">Review Rewards</p>
                  <p className="text-sm text-muted-foreground">Earn up to 25 pts per review + photos!</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit(mode === 'signup' ? onSignup : onLogin)} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <Label htmlFor="signup-name">Name *</Label>
                <Input
                  id="signup-name"
                  {...register('name', { required: mode === 'signup' ? 'Name is required' : false })}
                  className="mt-2"
                  data-testid="signup-name-input"
                />
                {errors.name && <p className="text-destructive text-sm mt-1">{errors.name.message}</p>}
              </div>
            )}
            <div>
              <Label htmlFor="signup-email">Email *</Label>
              <Input
                id="signup-email"
                type="email"
                {...register('email', { required: 'Email is required' })}
                className="mt-2"
                data-testid="signup-email-input"
              />
              {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
            </div>
            <div>
              <Label htmlFor="signup-password">Password *</Label>
              <div className="relative mt-2">
                <Input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { 
                    required: 'Password is required', 
                    minLength: mode === 'signup' ? { value: 6, message: 'Minimum 6 characters' } : undefined 
                  })}
                  className="pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-destructive text-sm mt-1">{errors.password.message}</p>}
            </div>
            
            {mode === 'login' && loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            {mode === 'login' && (
              <div className="text-right">
                <a 
                  href="/forgot-password"
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </a>
              </div>
            )}
            
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-full"
              data-testid="signup-submit-btn"
            >
              {isSubmitting 
                ? (mode === 'signup' ? 'Creating Account...' : 'Logging in...') 
                : (mode === 'signup' ? 'Sign Up & Get 15% Off' : 'Log In')
              }
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <button 
              onClick={switchMode}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {mode === 'signup' 
                ? 'Already have an account? Log in' 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
